import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Phone, ChatCircleText, FileText, CheckSquare, TrendUp, Crown, CurrencyDollar,
  CalendarPlus, Target as TargetIcon, PencilSimple, Check, X as XIcon,
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function StatCard({ label, value, sub, accent = "#2563eb", icon: Icon, testid }) {
  return (
    <motion.div
      whileHover={{ y: -2, x: -2 }}
      className="brut p-5 bg-white"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <div className="border-2 border-black p-1.5" style={{ background: accent }}>
          <Icon size={14} weight="bold" />
        </div>
      </div>
      <div className="font-display font-black text-4xl mt-3 tracking-tighter">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </motion.div>
  );
}

function GoalTile({
  testid, accent, icon: Icon, eyebrow, title, description,
  progress, target, unit, editing, onEdit, onCancel, onSave, draft, setDraft, saving,
}) {
  const pct = target > 0 ? Math.round(Math.min(100, (progress / target) * 100)) : 0;
  const hit = target > 0 && progress >= target;
  return (
    <div className="brut p-5 bg-white" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="border-2 border-black p-2.5 shrink-0" style={{ background: accent }}>
            <Icon size={20} weight="bold" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{eyebrow}</div>
            <div className="font-display font-black text-xl tracking-tighter leading-tight">{title}</div>
            <p className="text-xs text-zinc-600 mt-1">{description}</p>
          </div>
        </div>
        {!editing ? (
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1 border-2 border-black px-3 py-1.5 bg-white hover:bg-[#fde68a] font-bold uppercase text-[10px] tracking-widest shrink-0"
            data-testid={`${testid}-edit-btn`}
          >
            <PencilSimple size={12} weight="bold" /> {target > 0 ? "Edit" : "Set Goal"}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              type="number" min="0" step="1"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="0"
              className="border-2 border-black rounded-none h-9 focus-visible:ring-0 w-24"
              data-testid={`${testid}-input`}
            />
            <Button onClick={onSave} disabled={saving} className="rounded-none h-9 px-2 bg-[#22c55e] text-white border-2 border-black hover:bg-[#16a34a]" data-testid={`${testid}-save-btn`}>
              <Check size={14} weight="bold" />
            </Button>
            <Button onClick={onCancel} className="rounded-none h-9 px-2 bg-white text-black border-2 border-black hover:bg-zinc-100" data-testid={`${testid}-cancel-btn`}>
              <XIcon size={14} weight="bold" />
            </Button>
          </div>
        )}
      </div>
      {target > 0 ? (
        <div className="mt-4">
          <div className="flex items-end justify-between text-xs font-bold uppercase tracking-widest text-zinc-600 mb-1">
            <span>Progress</span>
            <span>
              {progress} / {target} {unit}
              <span className="ml-2 text-zinc-400">{pct}%</span>
              {hit && <span className="ml-2 text-[10px] font-black uppercase bg-[#22c55e] text-white border-2 border-black px-1.5 py-0.5">Goal Hit</span>}
            </span>
          </div>
          <div className="h-3 border-2 border-black bg-white overflow-hidden">
            <div className="h-full" style={{ width: `${pct}%`, background: hit ? "#22c55e" : "#2563eb" }} />
          </div>
        </div>
      ) : (
        <div className="mt-4 text-sm text-zinc-500">Set your goal to start tracking your pace.</div>
      )}
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [lb, setLb] = useState([]);
  const [recent, setRecent] = useState([]);
  const [editingGoal, setEditingGoal] = useState(null); // "weekly" | "marketing" | null
  const [goalDraft, setGoalDraft] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  const loadAll = useCallback(async () => {
    const [s, l, r] = await Promise.all([
      api.get("/stats/me"),
      api.get("/leaderboard?period=month"),
      api.get("/activities?limit=6"),
    ]);
    setStats(s.data);
    setLb(l.data);
    setRecent(r.data);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveGoal = async (field) => {
    setSavingGoal(true);
    try {
      const v = parseInt(goalDraft, 10);
      if (Number.isNaN(v) || v < 0) { toast.error("Enter a non-negative whole number"); return; }
      await api.patch(`/users/${user.id}/profile`, { [field]: v });
      toast.success("Goal saved");
      setEditingGoal(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save goal");
    } finally {
      setSavingGoal(false);
    }
  };

  const myRank = lb.find((x) => x.agent_id === user.id)?.rank || "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Dashboard</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1">
            Hey {user.name.split(" ")[0]}.
          </h1>
          <p className="text-zinc-600 mt-2">Here's your month. Keep the streak.</p>
        </div>
        <div className="brut px-4 py-3 bg-[#2563eb] flex items-center gap-2">
          <Crown size={20} weight="fill" />
          <span className="font-display font-black text-lg">RANK #{myRank}</span>
          <span className="text-xs font-bold uppercase tracking-widest">this month</span>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard testid="stat-points" label="Points" value={stats?.total_points ?? 0} accent="#2563eb" icon={TrendUp} sub="all-time" />
        <StatCard testid="stat-calls" label="Calls" value={stats?.total_calls ?? 0} accent="#fde68a" icon={Phone} sub="dialed" />
        <StatCard testid="stat-contacts" label="Contacts" value={stats?.total_contacts ?? 0} accent="#a7f3d0" icon={ChatCircleText} sub="conversations" />
        <StatCard testid="stat-appts" label="Appts Set" value={stats?.total_appts_set ?? 0} accent="#bae6fd" icon={CalendarPlus} sub="this period" />
        <StatCard testid="stat-cases-submitted" label="Cases Subm." value={stats?.total_cases_submitted ?? 0} accent="#fcd34d" icon={FileText} sub="submitted" />
        <StatCard testid="stat-cases-paid" label="Cases Paid" value={stats?.total_cases_paid ?? 0} accent="#86efac" icon={CheckSquare} sub="closed" />
      </div>

      {/* Premium row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard testid="stat-submitted-premium" label="Submitted Target Premium" value={fmt$(stats?.total_submitted_premium)} accent="#fecaca" icon={CurrencyDollar} sub="cumulative" />
        <StatCard testid="stat-paid-premium" label="Paid Target Premium" value={fmt$(stats?.total_paid_premium)} accent="#fca5a5" icon={CurrencyDollar} sub="cumulative" />
      </div>

      {/* Personal Goal Tile: Weekly Appointment Goal */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GoalTile
          testid="goal-weekly-appts"
          accent="#bae6fd"
          icon={CalendarPlus}
          eyebrow="This Week"
          title="Weekly Appointment Goal"
          description="Appointments set this week vs. your weekly target."
          progress={stats?.weekly_appointment_goal?.progress || 0}
          target={stats?.weekly_appointment_goal?.target || 0}
          unit="appts"
          editing={editingGoal === "weekly"}
          onEdit={() => { setGoalDraft(String(stats?.weekly_appointment_goal?.target || 10)); setEditingGoal("weekly"); }}
          onCancel={() => setEditingGoal(null)}
          onSave={() => saveGoal("weekly_appointment_goal")}
          draft={goalDraft}
          setDraft={setGoalDraft}
          saving={savingGoal}
        />
      </div>

      {/* MP team rollup */}
      {stats?.team && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">My Team</div>
              <div className="font-display font-black text-2xl tracking-tight">Downline Production · {stats.team.size} agents</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard testid="team-points" label="Team Points" value={stats.team.total_points} accent="#2563eb" icon={TrendUp} sub="combined" />
            <StatCard testid="team-calls" label="Team Calls" value={stats.team.total_calls} accent="#fde68a" icon={Phone} sub="dialed" />
            <StatCard testid="team-contacts" label="Team Contacts" value={stats.team.total_contacts} accent="#a7f3d0" icon={ChatCircleText} sub="" />
            <StatCard testid="team-appts" label="Team Appts" value={stats.team.total_appts_set} accent="#bae6fd" icon={CalendarPlus} sub="set" />
            <StatCard testid="team-cases-submitted" label="Team Cases Sub." value={stats.team.total_cases_submitted} accent="#fcd34d" icon={FileText} sub="" />
            <StatCard testid="team-cases-paid" label="Team Cases Paid" value={stats.team.total_cases_paid} accent="#86efac" icon={CheckSquare} sub="" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend chart */}
        <div className="brut p-5 bg-white lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">7-Day Points</div>
              <div className="font-display font-black text-2xl tracking-tight">Your Streak</div>
            </div>
          </div>
          <div className="h-64 -ml-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.trend || []}>
                <CartesianGrid stroke="#09090b" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#09090b" strokeWidth={2} style={{ fontSize: 11, fontWeight: 700 }} />
                <YAxis stroke="#09090b" strokeWidth={2} style={{ fontSize: 11, fontWeight: 700 }} />
                <Tooltip contentStyle={{ border: "2px solid #09090b", borderRadius: 0, boxShadow: "4px 4px 0 0 #09090b" }} />
                <Line type="monotone" dataKey="points" stroke="#09090b" strokeWidth={3} dot={{ fill: "#2563eb", stroke: "#09090b", strokeWidth: 2, r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top 3 snapshot */}
        <div className="brut p-5 bg-[#09090b] text-white">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">This Month</div>
          <div className="font-display font-black text-2xl tracking-tight mb-4">TOP 3</div>
          <div className="space-y-3">
            {lb.slice(0, 3).map((a, i) => {
              const color = ["#2563eb", "#e4e4e7", "#fdba74"][i];
              return (
                <div key={a.agent_id} className="flex items-center gap-3 border-2 border-white p-2 bg-zinc-900" data-testid={`top-${i + 1}`}>
                  <div className="w-9 h-9 border-2 border-white flex items-center justify-center font-black" style={{ background: color, color: "#09090b" }}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{a.agent_name}</div>
                    <div className="text-xs text-zinc-400">{a.cases_submitted || 0} subm · {a.cases_paid || 0} paid</div>
                  </div>
                  <div className="font-display font-black text-lg">{a.points}</div>
                </div>
              );
            })}
            {lb.length === 0 && <div className="text-sm text-zinc-400">No activity yet this month.</div>}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="brut p-5 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Recent</div>
            <div className="font-display font-black text-2xl tracking-tight">Activity Feed</div>
          </div>
        </div>
        <div className="space-y-2">
          {recent.length === 0 && <div className="text-sm text-zinc-500">Nothing logged yet. Head to Log Activity.</div>}
          {recent.map((a) => (
            <div key={a.id} className="flex items-center justify-between border-2 border-black px-3 py-2 bg-[#f4f4f5]">
              <div className="flex items-center gap-3">
                <div className="px-2 py-1 bg-[#09090b] text-white text-[10px] font-bold uppercase tracking-widest">
                  {a.activity_type.replace(/_/g, " ")}
                </div>
                <div className="text-sm">
                  {a.client_name && <span className="text-zinc-600">{a.client_name}</span>}
                  {a.count && a.count > 1 ? <span className="text-zinc-600"> · ×{a.count}</span> : null}
                  {a.premium_amount ? <span className="text-zinc-600"> · {fmt$(a.premium_amount)}</span> : null}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-black text-sm">+{a.points}</span>
                <span className="text-xs text-zinc-500">{new Date(a.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
